import {DirectPurchaseOrderRequest} from '../core/_models'

export type DirectPoCategoryKind = 'NONE' | 'GENERAL_STORES' | 'SPARES' | 'SERVICES'
export type DirectPoRequestFor = 'GENERAL_STORES' | 'TECHNICAL'

export type DirectPoLineDraft = {
  id?: number
  itemSearchText: string
  serviceHeadId: string
  serviceSubHeadId: string
  inventoryItemId?: number | null
  masterInventoryItemId?: number | null
  inventoryItemSubCategoryId?: number | null
  robDisplay: string
  equipmentId: string
  equipmentName: string
  componentId: string
  componentName: string
  partId: string
  partName: string
  manualItemName: string
  unitOfMeasurement: string
  quantity: string
  unitPrice: string
  assignedQuantity: string
  notes: string
  logisticsAccountCodeId: string
  logisticsSubaccountCodeId: string
  budgetAccountCodeId: string
  budgetSubaccountCodeId: string
  supplyCapacity: string
  deliveryTimeDays: string
  lineFiles: File[]
  existingAttachments: Array<{id?: number; fileName?: string | null}>
}

export type DirectPoFormDraft = {
  title: string
  description: string
  requisitionDescription: string
  rfqTitle: string
  rfqItemDescription: string
  technicalSpecifications: string
  requisitionType: string
  requestFor: DirectPoRequestFor
  vesselId: string
  categoryId: string
  requestedPortId: string
  deliveryPortId: string
  requiredDeliveryDate: string
  responseDueDate: string
  promisedDeliveryDate: string
  vendorId: string
  vendorCompanyMappingId: string
  poDate: string
  supplierReference: string
  logisticAccountCode: string
  invoiceAddressedTo: string
  poRaisedByCompany: string
  poRaisedByUser: string
  accountCategory: string
  deliveryDate: string
  deliveryTerms: string
  paymentTerms: string
  termsOfPayment: string
  terms: string
  specialInstructions: string
  remittanceInstructions: string
  currency: string
  quotationCurrency: string
  companyCurrencyCode: string
  exchangeRate: string
  exchangeRateDate: string
  exchangeRateDirect: boolean
  totalDiscount: string
  freightCost: string
  packagingCost: string
  truckCost: string
  bargeCost: string
  otherCost: string
  vatOnLogistics: string
}

export const deriveDirectPoCategoryKind = (categoryName?: string | null): DirectPoCategoryKind => {
  const label = String(categoryName || '').trim().toUpperCase()
  if (!label) return 'NONE'
  if (label.includes('SERVICE')) return 'SERVICES'
  if (label.includes('SPARE')) return 'SPARES'
  if (label.includes('STORE') || label.includes('GENERAL')) return 'GENERAL_STORES'
  return 'NONE'
}

export const deriveRequestForFromCategoryKind = (
  kind: DirectPoCategoryKind
): DirectPoRequestFor => (kind === 'GENERAL_STORES' ? 'GENERAL_STORES' : 'TECHNICAL')

export const createEmptyDirectPoLine = (): DirectPoLineDraft => ({
  itemSearchText: '',
  serviceHeadId: '',
  serviceSubHeadId: '',
  inventoryItemId: null,
  masterInventoryItemId: null,
  inventoryItemSubCategoryId: null,
  robDisplay: '',
  equipmentId: '',
  equipmentName: '',
  componentId: '',
  componentName: '',
  partId: '',
  partName: '',
  manualItemName: '',
  unitOfMeasurement: '',
  quantity: '1',
  unitPrice: '',
  assignedQuantity: '',
  notes: '',
  logisticsAccountCodeId: '',
  logisticsSubaccountCodeId: '',
  budgetAccountCodeId: '',
  budgetSubaccountCodeId: '',
  supplyCapacity: '',
  deliveryTimeDays: '',
  lineFiles: [],
  existingAttachments: [],
})

export const lineHasMeaningfulData = (line: DirectPoLineDraft) =>
  Boolean(
    line.itemSearchText.trim() ||
      line.serviceHeadId ||
      line.serviceSubHeadId ||
      line.inventoryItemId ||
      line.masterInventoryItemId ||
      line.equipmentId ||
      line.componentId ||
      line.partId ||
      line.manualItemName.trim() ||
      (line.quantity && line.quantity !== '1') ||
      line.unitPrice.trim() ||
      line.notes.trim() ||
      line.lineFiles.length
  )

export const resetLineForCategoryKind = (
  line: DirectPoLineDraft,
  kind: DirectPoCategoryKind
): DirectPoLineDraft => {
  const common = {
    logisticsAccountCodeId: line.logisticsAccountCodeId,
    logisticsSubaccountCodeId: line.logisticsSubaccountCodeId,
    budgetAccountCodeId: line.budgetAccountCodeId,
    budgetSubaccountCodeId: line.budgetSubaccountCodeId,
    quantity: line.quantity || '1',
    unitOfMeasurement: line.unitOfMeasurement,
    unitPrice: line.unitPrice,
    assignedQuantity: line.assignedQuantity,
    notes: line.notes,
    supplyCapacity: line.supplyCapacity,
    deliveryTimeDays: line.deliveryTimeDays,
    lineFiles: line.lineFiles,
    existingAttachments: line.existingAttachments,
  }

  if (kind === 'GENERAL_STORES') {
    return {
      ...createEmptyDirectPoLine(),
      ...common,
      manualItemName: line.manualItemName,
      itemSearchText: line.itemSearchText,
      inventoryItemId: line.inventoryItemId ?? null,
      masterInventoryItemId: line.masterInventoryItemId ?? null,
      inventoryItemSubCategoryId: null,
      robDisplay: line.robDisplay,
    }
  }

  if (kind === 'SPARES') {
    return {
      ...createEmptyDirectPoLine(),
      ...common,
      manualItemName: line.manualItemName,
      equipmentId: line.equipmentId,
      equipmentName: line.equipmentName,
      componentId: line.componentId,
      componentName: line.componentName,
      partId: line.partId,
      partName: line.partName,
    }
  }

  if (kind === 'SERVICES') {
    return {
      ...createEmptyDirectPoLine(),
      ...common,
      serviceHeadId: line.serviceHeadId,
      serviceSubHeadId: line.serviceSubHeadId,
      manualItemName: line.manualItemName,
    }
  }

  return {
    ...createEmptyDirectPoLine(),
    ...common,
  }
}

const toNumberOrUndefined = (value: string) => {
  const trimmed = String(value || '').trim()
  if (!trimmed) return undefined
  const parsed = Number(trimmed)
  return Number.isFinite(parsed) ? parsed : undefined
}

export const buildDirectPurchaseOrderPayload = (
  form: DirectPoFormDraft,
  lines: DirectPoLineDraft[],
  categoryKind: DirectPoCategoryKind
): DirectPurchaseOrderRequest => ({
  title: form.title || undefined,
  description: form.description || undefined,
  requisitionDescription: form.requisitionDescription || undefined,
  rfqTitle: form.rfqTitle || undefined,
  rfqItemDescription: form.rfqItemDescription || undefined,
  technicalSpecifications: form.technicalSpecifications || undefined,
  requisitionType: form.requisitionType || undefined,
  requestFor: deriveRequestForFromCategoryKind(categoryKind),
  vesselId: Number(form.vesselId),
  categoryId: toNumberOrUndefined(form.categoryId),
  requestedPortId: Number(form.requestedPortId),
  deliveryPortId: toNumberOrUndefined(form.deliveryPortId),
  requiredDeliveryDate: form.requiredDeliveryDate || undefined,
  responseDueDate: form.responseDueDate || undefined,
  promisedDeliveryDate: form.promisedDeliveryDate || undefined,
  vendorId: toNumberOrUndefined(form.vendorId),
  vendorCompanyMappingId: toNumberOrUndefined(form.vendorCompanyMappingId),
  poDate: form.poDate || undefined,
  supplierReference: form.supplierReference || undefined,
  logisticAccountCode: form.logisticAccountCode || undefined,
  invoiceAddressedTo: form.invoiceAddressedTo || undefined,
  poRaisedByCompany: form.poRaisedByCompany || undefined,
  poRaisedByUser: form.poRaisedByUser || undefined,
  accountCategory: form.accountCategory || undefined,
  deliveryDate: form.deliveryDate || undefined,
  deliveryTerms: form.deliveryTerms || undefined,
  paymentTerms: form.paymentTerms || undefined,
  termsOfPayment: form.termsOfPayment || undefined,
  terms: form.terms || undefined,
  specialInstructions: form.specialInstructions || undefined,
  remittanceInstructions: form.remittanceInstructions || undefined,
  currency: form.currency || undefined,
  quotationCurrency: form.quotationCurrency || undefined,
  companyCurrencyCode: form.companyCurrencyCode || undefined,
  exchangeRate:
    form.quotationCurrency && form.companyCurrencyCode &&
    form.quotationCurrency.toUpperCase() === form.companyCurrencyCode.toUpperCase()
      ? 1
      : toNumberOrUndefined(form.exchangeRate),
  exchangeRateDate:
    form.quotationCurrency && form.companyCurrencyCode && form.quotationCurrency.toUpperCase() !== form.companyCurrencyCode.toUpperCase()
      ? form.exchangeRateDate || undefined
      : undefined,
  exchangeRateDirect:
    form.quotationCurrency && form.companyCurrencyCode && form.quotationCurrency.toUpperCase() !== form.companyCurrencyCode.toUpperCase()
      ? form.exchangeRateDirect
      : undefined,
  totalDiscount: toNumberOrUndefined(form.totalDiscount),
  freightCost: toNumberOrUndefined(form.freightCost),
  packagingCost: toNumberOrUndefined(form.packagingCost),
  truckCost: toNumberOrUndefined(form.truckCost),
  bargeCost: toNumberOrUndefined(form.bargeCost),
  otherCost: toNumberOrUndefined(form.otherCost),
  vatOnLogistics: toNumberOrUndefined(form.vatOnLogistics),
  lines: lines.map((line) => {
    const base = {
      id: line.id,
      manualItemName: line.manualItemName || undefined,
      unitOfMeasurement: line.unitOfMeasurement || undefined,
      quantity: Number(line.quantity),
      unitPrice: Number(line.unitPrice),
      assignedQuantity: toNumberOrUndefined(line.assignedQuantity),
      notes: line.notes || undefined,
      logisticsAccountCodeId: toNumberOrUndefined(line.logisticsAccountCodeId),
      logisticsSubaccountCodeId: toNumberOrUndefined(line.logisticsSubaccountCodeId),
      budgetAccountCodeId: toNumberOrUndefined(line.budgetAccountCodeId),
      budgetSubaccountCodeId: toNumberOrUndefined(line.budgetSubaccountCodeId),
      supplyCapacity: toNumberOrUndefined(line.supplyCapacity),
      deliveryTimeDays: toNumberOrUndefined(line.deliveryTimeDays),
    }

    if (categoryKind === 'GENERAL_STORES') {
      return {
        ...base,
        inventoryItemId: line.inventoryItemId ?? undefined,
        masterInventoryItemId: line.masterInventoryItemId ?? undefined,
      }
    }

    if (categoryKind === 'SPARES') {
      return {
        ...base,
        partId: toNumberOrUndefined(line.partId),
        subcomponentId: toNumberOrUndefined(line.componentId),
      }
    }

    if (categoryKind === 'SERVICES') {
      return {
        ...base,
        inventoryItemHeadId: toNumberOrUndefined(line.serviceHeadId),
        inventoryItemSubHeadId: toNumberOrUndefined(line.serviceSubHeadId),
      }
    }

    return base
  }),
})
